/**
 * visualizerAtoms - Atoms for expand/collapse group state, viewport sizing,
 * and layout-force signalling within the ObjectsVisualizer.
 */

import { atom, type PrimitiveAtom, type WritableAtom } from 'jotai';

// ============================================================================
// Group expand/collapse
// ============================================================================

/** Set of raw GraphQL typenames whose groups are currently expanded. */
export const ExpandedGroupsAtom: PrimitiveAtom<Set<string>> & {
	init: Set<string>;
} = atom<Set<string>>(new Set<string>());

/**
 * Tracks the typename of the group that was most recently expanded,
 * so the viewport can be centered on it. null after a collapse or reset.
 */
export const LastExpandedGroupAtom: PrimitiveAtom<string | null> & {
	init: string | null;
} = atom<string | null>(null);

/** Write atom that clears all expanded groups and the last-expanded tracker.
 *  No-op when already empty to avoid triggering unnecessary re-renders. */
export const ResetExpandedGroupsAtom: WritableAtom<null, [], void> & {
	init: null;
} = atom(null, (get, set) => {
	const current = get(ExpandedGroupsAtom);
	if (current.size === 0 && get(LastExpandedGroupAtom) === null) {
		return;
	}
	set(ExpandedGroupsAtom, new Set());
	set(LastExpandedGroupAtom, null);
});

// ============================================================================
// Viewport size
// ============================================================================

export const ViewportSizeAtom: PrimitiveAtom<{
	width: number;
	height: number;
}> & {
	init: {
		width: number;
		height: number;
	};
} = atom<{ width: number; height: number }>({
	width: 1920,
	height: 1080,
});

// ============================================================================
// Layout force flag
//
// When true, the next ReactFlow sync effect should use layout-computed
// positions for all nodes instead of preserving user-dragged positions.
// Written by the graph-data sync effect, read+cleared by the RF sync effect.
// ============================================================================

export const ForceLayoutPositionsAtom: PrimitiveAtom<boolean> & {
	init: boolean;
} = atom<boolean>(false);
